<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import type { NotificationDto } from '@roadfootage/api-client'

import { useNotificationsStore } from '@/stores/notifications'
import { formatDateTime, formatRelativeTime } from '@/utils/format'
import { isAppPath } from '@/utils/app-path'

// The notification list: newest first, tap to mark read and open its link. Shared by the bell menu.
const emit = defineEmits<{ navigate: [] }>()

const router = useRouter()
const notificationsStore = useNotificationsStore()
const { items, unreadCount, hasMore, isLoadingOlder } = storeToRefs(notificationsStore)

// Links come from the server as app paths. Never follow anything that could leave the app (isAppPath).
async function open(notification: NotificationDto) {
  if (notification.id) void notificationsStore.markRead(notification.id)
  if (isAppPath(notification.linkPath)) await router.push(notification.linkPath)
  emit('navigate')
}
</script>

<template>
  <div class="notification-list" data-testid="notification-list">
    <div class="row items-center no-wrap q-px-md q-py-xs">
      <div class="text-subtitle1 text-weight-medium col">Notifications</div>
      <q-btn
        flat
        dense
        no-caps
        color="primary"
        label="Mark all read"
        :disable="unreadCount === 0"
        data-testid="notifications-mark-all"
        @click="notificationsStore.markAllRead()"
      />
    </div>
    <q-separator />

    <div
      v-if="items.length === 0"
      class="text-center text-grey-7 q-pa-xl"
      data-testid="notifications-empty"
    >
      No notifications yet
    </div>

    <q-list role="list" v-else separator>
      <q-item
        v-for="notification in items"
        :key="notification.id"
        clickable
        v-ripple
        data-testid="notification-item"
        :data-unread="!notification.readAt"
        @click="open(notification)"
      >
        <q-item-section side top class="q-pr-sm notification-dot-column">
          <span v-if="!notification.readAt" class="notification-dot" aria-hidden="true" />
        </q-item-section>
        <q-item-section>
          <q-item-label :class="notification.readAt ? '' : 'text-weight-bold'">
            <span v-if="!notification.readAt" class="sr-only">Unread: </span
            >{{ notification.title }}
          </q-item-label>
          <q-item-label caption lines="3">{{ notification.body }}</q-item-label>
        </q-item-section>
        <q-item-section side top>
          <q-item-label caption :title="formatDateTime(notification.createdAt)">
            {{ formatRelativeTime(notification.createdAt) }}
          </q-item-label>
        </q-item-section>
      </q-item>
    </q-list>

    <div v-if="hasMore" class="text-center q-pa-sm">
      <q-btn
        flat
        no-caps
        color="primary"
        label="Show older"
        :loading="isLoadingOlder"
        data-testid="notifications-show-older"
        @click="notificationsStore.loadOlder()"
      />
    </div>
  </div>
</template>

<style scoped>
.notification-list {
  width: 100%;
  min-width: 0;
}

.notification-dot-column {
  min-width: 14px;
}

.notification-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-top: 4px;
  border-radius: 50%;
  background: var(--q-primary);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
